import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { CampaignCode, useUpdateCampaignCode } from "@/lib/api/campaign-codes";
import { Loader2 } from "lucide-react";

interface EditCampaignCodeDialogProps {
  campaignCode: CampaignCode | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const createFormSchema = (redeemedCount: number) => z.object({
  slots: z.coerce
    .number()
    .min(redeemedCount, `Cannot reduce below redeemed count (${redeemedCount})`)
    .max(100000, "Maximum 100,000 slots"),
  description: z.string().max(500, "Description must be 500 characters or less").optional(),
  enabled: z.boolean(),
});

type FormValues = z.infer<ReturnType<typeof createFormSchema>>;

export function EditCampaignCodeDialog({
  campaignCode,
  open,
  onOpenChange,
}: EditCampaignCodeDialogProps) {
  const { mutateAsync: updateCampaignCode, isPending } = useUpdateCampaignCode();
  const redeemedCount = campaignCode?.redeemed_count ?? 0;

  const formSchema = useMemo(() => createFormSchema(redeemedCount), [redeemedCount]);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      slots: campaignCode?.slots ?? 100,
      description: campaignCode?.description ?? "",
      enabled: campaignCode?.enabled ?? true,
    },
  });

  const campaignCodeId = campaignCode?.id;
  const campaignCodeSlots = campaignCode?.slots;
  const campaignCodeDescription = campaignCode?.description;
  const campaignCodeEnabled = campaignCode?.enabled;

  useEffect(() => {
    if (campaignCodeId) {
      form.reset({
        slots: campaignCodeSlots ?? 100,
        description: campaignCodeDescription ?? "",
        enabled: campaignCodeEnabled ?? true,
      });
    }
  }, [campaignCodeId, campaignCodeSlots, campaignCodeDescription, campaignCodeEnabled]);

  const onSubmit = async (values: FormValues) => {
    if (!campaignCode) return;
    await updateCampaignCode({
      campaign_code_id: campaignCode.id,
      slots: values.slots,
      description: values.description || undefined,
      enabled: values.enabled,
    });
    onOpenChange(false);
  };

  if (!campaignCode) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Campaign Code: {campaignCode.code}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="rounded-md bg-muted p-3">
              <p className="text-sm text-muted-foreground">
                Code: <span className="font-mono font-medium text-foreground">{campaignCode.code}</span>
              </p>
              <p className="text-sm text-muted-foreground">
                Redeemed: <span className="font-medium text-foreground">{redeemedCount}</span> / {campaignCode.slots}
              </p>
            </div>
            <FormField
              control={form.control}
              name="slots"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Slot Count</FormLabel>
                  <FormControl>
                    <Input type="number" min={redeemedCount} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Internal notes..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="enabled"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-3">
                  <div>
                    <FormLabel>Active</FormLabel>
                    <p className="text-sm text-muted-foreground">
                      {field.value
                        ? "Code can be redeemed by new users"
                        : "Code is disabled and cannot be redeemed"}
                    </p>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isPending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
